import type { AutomationTrigger, CampaignAutomation } from '../../api/types';

/**
 * What the Automations screens say about an automation, kept pure so the list
 * card, the editor's flow and the failed-run banner word one automation the
 * same way — and so the wording is tested rather than eyeballed.
 */

type Shape = Pick<CampaignAutomation, 'trigger' | 'delayHours' | 'lapsedDays'>;

const hoursPhrase = (hours: number) =>
  hours % 24 === 0
    ? `${hours / 24} day${hours === 24 ? '' : 's'}`
    : `${hours} hour${hours === 1 ? '' : 's'}`;

/** One line for the list: "1 day after each trip", "90 days after their last trip". */
export function triggerLine(automation: Shape): string {
  if (automation.trigger === 'lapsed') return `${automation.lapsedDays ?? 90} days after their last trip`;
  const delay = automation.delayHours ? hoursPhrase(automation.delayHours) : null;
  if (automation.trigger === 'first_booking') {
    return delay ? `${delay} after a first reservation` : 'Right after a first reservation';
  }
  return delay ? `${delay} after each trip` : 'Right after each trip';
}

/** The flow's first node: what sets it off. */
export const WHEN_TITLES: Record<AutomationTrigger, string> = {
  trip_completed: 'A trip is finished',
  first_booking: 'Somebody books for the first time',
  lapsed: 'A customer goes quiet',
};

/** Under it: who, and how often one person can be sent it. */
export function whenDetail(trigger: AutomationTrigger, audienceType: 'customers' | 'affiliates'): string {
  const who = audienceType === 'affiliates' ? 'Affiliates' : 'Customers';
  if (trigger === 'trip_completed') return `${who} · each trip counts`;
  if (trigger === 'first_booking') return `${who} · once ever`;
  return `${who} · again if they come back and go quiet again`;
}

/**
 * The flow's second node. A win-back's wait IS its trigger — days since the
 * last trip — so it reads as that rather than as a delay after something.
 */
export function waitTitle(automation: Shape): string {
  if (automation.trigger === 'lapsed') {
    return `${automation.lapsedDays ?? 90} days since their last trip, nothing booked`;
  }
  const after = automation.trigger === 'trip_completed' ? 'the pickup' : 'the reservation';
  return automation.delayHours ? `${hoursPhrase(automation.delayHours)} after ${after}` : `Right after ${after}`;
}

const clock = (minutes: number) =>
  `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;

/** The flow's last node: the rules every send keeps, whatever the step above says. */
export function alwaysTitle(
  automation: Pick<CampaignAutomation, 'sendWindowStart' | 'sendWindowEnd' | 'frequencyCapHours'>,
): string {
  const parts: string[] = [];
  if (automation.sendWindowStart != null && automation.sendWindowEnd != null) {
    parts.push(`Only ${clock(automation.sendWindowStart)}–${clock(automation.sendWindowEnd)}`);
  } else {
    parts.push('Any time of day');
  }
  if (automation.frequencyCapHours) {
    const hours = automation.frequencyCapHours;
    parts.push(`skip anyone contacted in ${hoursPhrase(hours)}`);
  }
  return parts.join(' · ');
}

/**
 * "15 min ago", "3 hours ago", "2 days ago" — a DURATION, which reads the same
 * in every zone. Past a week the caller's tenant-formatted date takes over,
 * because "41 days ago" makes the reader do arithmetic the date would not.
 */
export function sinceLabel(iso: string | null, formatted: (iso: string) => string, now: Date = new Date()): string {
  if (!iso) return '';
  const ms = now.getTime() - new Date(iso).getTime();
  if (!Number.isFinite(ms)) return '';
  const minutes = Math.floor(ms / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
  return formatted(iso);
}

/**
 * What the card says under the name: what it last did, or why it is not doing
 * anything. A paused automation says who paused it — switching it on again is a
 * decision somebody already made the other way.
 */
export function lastRunLine(
  automation: Pick<CampaignAutomation, 'status' | 'lastRun' | 'lastSweptAt' | 'pausedAt' | 'pausedBy'>,
  since: (iso: string | null) => string,
  nameOf: (dashBoardId: number) => string | null,
): string {
  if (automation.status === 'draft') return 'Not switched on yet';
  if (automation.status === 'paused') {
    const who = automation.pausedBy ? nameOf(automation.pausedBy) : null;
    if (!automation.pausedAt) return 'Paused';
    return `Paused ${since(automation.pausedAt)}${who ? ` by ${who}` : ''}`;
  }
  const run = automation.lastRun;
  if (!run) {
    return automation.lastSweptAt
      ? `Checked ${since(automation.lastSweptAt)} · nobody new to send to yet`
      : 'On · first check within 15 minutes';
  }
  const sent = run.sent ? `${run.sent.toLocaleString()} sent` : 'nothing sent';
  return `Last run ${since(run.at)} · ${sent}`;
}

export type ProblemKind = 'reconnect' | 'access' | 'dispatch' | 'refused' | 'run_failed';

export interface AutomationProblem {
  kind: ProblemKind;
  /** The sentence the banner leads with. */
  title: string;
  /** What happened, in the service's words or ours. */
  detail: string;
  /** People the refused sweep left waiting, when the service knew. */
  waiting: number | null;
}

/**
 * Why an automation that is on is not sending, and therefore which fix the
 * banner offers. Read from what the service recorded, never guessed from
 * silence: no `lastError` and a newest run that did not fail is no problem.
 *
 * - **reconnect** — the sending mailbox or number needs signing in again. Told
 *   from the inbox's own flag rather than from the error's words, because the
 *   reason a launch was refused is whatever that provider said.
 * - **access** — the person who switched it on can no longer send from there.
 *   The fix is somebody who can switching it on again.
 * - **dispatch** — dispatch could not be read. Nothing for the operator to fix;
 *   the next sweep asks again.
 * - **refused** — any other refusal, with its reason.
 * - **run_failed** — the sweep launched a run and the RUN failed part way
 *   (a grant that lapsed mid-send). Opening that run is the way in.
 */
export function automationProblem(
  automation: Pick<CampaignAutomation, 'status' | 'lastError' | 'lastErrorWaiting' | 'lastRun'>,
  inboxNeedsReauth: boolean,
): AutomationProblem | null {
  if (automation.status !== 'active') return null;
  const waiting = automation.lastErrorWaiting ?? null;
  const error = automation.lastError;
  if (error) {
    if (inboxNeedsReauth) {
      return { kind: 'reconnect', title: 'Couldn’t send its last run', detail: error, waiting };
    }
    if (/no longer has send access/i.test(error)) {
      return { kind: 'access', title: 'Couldn’t send its last run', detail: error, waiting };
    }
    if (/^Could not read dispatch/i.test(error)) {
      return {
        kind: 'dispatch',
        title: 'Couldn’t check who qualifies',
        detail: 'Dispatch did not answer. It asks again every 15 minutes, and nobody is skipped meanwhile.',
        waiting: null,
      };
    }
    return { kind: 'refused', title: 'Couldn’t send its last run', detail: error, waiting };
  }
  if (automation.lastRun?.status === 'failed') {
    return {
      kind: 'run_failed',
      title: 'Its last run stopped part way',
      detail: automation.lastRun.error || 'The run failed before it reached everybody.',
      waiting: null,
    };
  }
  return null;
}
